import { ApolloCache, ApolloQueryResult, InMemoryCache, useApolloClient } from '@apollo/client';
import { Button, IllustratedMessage, Pagination, Progress, Stack, Switch } from '@eleveo/components-react';
import { FilterIcon, SearchNoneIcon } from '@eleveo/icons-react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { toZoned } from '@internationalized/date';
import { FormApi } from 'final-form';
import cloneDeep from 'lodash.clonedeep';
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useDateFormatter } from 'react-aria';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router';
import { toast } from 'react-toastify';

import { Permissions, useAuth } from '../../app/AuthorizationProvider';
import { Header } from '../../components/LayoutNext/Header';
import { Content, Panel, SidebarWrapper } from '../../components/LayoutNext/LayoutNext';
import { ActiveFilters as ActiveFiltersNew } from '../../components/LayoutNext/components/ActiveFilters';
import { Center } from '../../components/LayoutNext/components/Center';
import {
  getActiveFilterValue,
  stateItems,
  subtypeItems,
} from '../../components/LayoutNext/components/CheckboxFilterTile';
import { ModalConfirmationNewStyle as ModalConfirmation } from '../../components/ModalConfirmationNewStyle';
import { useSidebar } from '../../components/layout/Sidebar';
import {
  GetNotificationSettingsDocument,
  GetSupervisorPtoRequestDocument,
  GetSupervisorRequestsDocument,
  GetSupervisorShiftSwapRequestDocument,
  GetSupervisorShiftSwapRequestQuery,
  NotificationFeature,
  NotificationType,
  Queue,
  RequestState,
  RequestSubtype,
  RequestType,
  ShiftSwapRequestAction,
  ShiftSwapRequestController,
  useGetNotificationSettingsQuery,
  useGetQueuesQuery,
  useGetSupervisorRequestsQuery,
  useUpdateStateOfNotificationSettingsMutation,
  useUpdateStateOfShiftSwapRequestMutation,
  useUpdateStateOfSupervisorPtoRequestMutation,
} from '../../graphql/components';
import { QueuesResult } from '../../graphql/types';
import { PATH_REQUESTS } from '../../router/routes';
import { recordBy } from '../../utils/array';
import { useFilter } from '../../utils/filter';
import { getRequestKey } from '../Scheduling/AgentsView/utils';
import {
  RequestsFilterSidebar,
  RequestsSidebarFilterState,
  getDefaultRequestsSidebarFilter,
} from './RequestsFilterSidebar';
import { RequestsHeaderFilterType, RequestsHeaderSearchForm } from './RequestsHeaderSearchForm';
import { RequestsSidebarPTODetail } from './RequestsSidebarPTODetail';
import { RequestsSidebarShiftSwapDetail } from './RequestsSidebarShiftSwapDetail';
import { RequestsTable } from './RequestsTable';
import {
  CONTAINER_TAG,
  EMAIL_NOTIFICATION_SWITCH_TAG,
  LIST_PAGINATION_TAG,
  TITLE_TAG,
  TOGGLE_FILTER_BUTTON_TAG,
} from './constants';

import type {
  GetSupervisorPtoRequestQuery,
  GetSupervisorRequestsQueryResult,
  PtoRequestSubtype,
  SupervisorRequestPreview,
} from '../../graphql/components';
import type { Values } from '../../types';

export type DisableEmailNotificationsModal =
  | { state: 'ready' }
  | {
      state: 'open';
    };

export const REQUEST_URL_KEYS = {
  [RequestType.Pto]: 'absence',
  [RequestType.ShiftSwap]: 'shift-swap',
} as const;

// reverse to REQUEST_URL_KEYS
export const REQUEST_URL_KEYS_TO_TYPES = Object.entries(REQUEST_URL_KEYS).reduce(
  (values, [key, value]) => ({
    ...values,
    [value]: key as RequestType,
  }),
  {} as Record<Values<typeof REQUEST_URL_KEYS>, RequestType>
);

export const RequestsScene: React.FC = () => {
  const { t } = useTranslation();

  const { userId, userHasPermissions, timezone } = useAuth();

  const { openSidebar, closeSidebar, isSidebarOpen } = useSidebar();

  const client = useApolloClient();

  const headerForm = useRef<FormApi<RequestsHeaderFilterType>>();

  const { typeKey, requestId: requestIdToView } = useParams<{
    typeKey?: Values<typeof REQUEST_URL_KEYS>;
    requestId?: string;
  }>();
  const routerHistory = useHistory();

  const typeOfRequestToView = typeKey && REQUEST_URL_KEYS_TO_TYPES[typeKey];

  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 25,
  });

  // Filter's states
  const {
    filter: searchFilter,
    setFilter: setSearchFilter,
    isFilterActive: isHeaderFilterActive,
  } = useFilter<RequestsHeaderFilterType>();
  const {
    filter: sidebarFilter,
    setFilter: setSidebarFilter,
    isFilterActive: isSidebarFiltersActive,
  } = useFilter<RequestsSidebarFilterState>(getDefaultRequestsSidebarFilter(timezone));

  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false);
  const [preOpenedItemIds, setPreOpenedItemIds] = useState<string[]>([]);

  useLayoutEffect(() => {
    if (isFilterSidebarOpen) {
      return;
    }

    // Open sidebar with detail view of required request params
    if (requestIdToView && typeOfRequestToView && !isSidebarOpen) {
      openSidebar();
      return;
    }

    // Close sidebar after request params were cleaned-up
    if (!requestIdToView && !typeOfRequestToView && isSidebarOpen) {
      closeSidebar();
      return;
    }

    // When not supported typeKey provided, show warning toast message and redirect
    if (!typeOfRequestToView && typeKey) {
      toast.warning(t('schedule.request.unknownSelfServiceRequestType'));
      routerHistory.push(PATH_REQUESTS);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSidebarOpen, requestIdToView, typeOfRequestToView, openSidebar, closeSidebar, typeKey]);

  const getSupervisorRequestsVariables = useMemo(() => {
    const { types, subtypes } = (sidebarFilter.subtypes ?? []).reduce(
      (values, type) => {
        if (type === RequestSubtype.Approver) {
          values.types.push(RequestType.ShiftSwap);
        } else {
          // PTO type
          if (!values.types.includes(RequestType.Pto)) {
            values.types.push(RequestType.Pto);
          }

          values.subtypes.push(type as unknown as PtoRequestSubtype);
        }

        return values;
      },
      { types: [] as RequestType[], subtypes: [] as PtoRequestSubtype[] }
    );

    return {
      agentName: searchFilter.agentName,
      agentSurname: searchFilter.agentSurname,
      createdAtFrom: sidebarFilter.createdRange?.start
        ? toZoned(sidebarFilter.createdRange?.start, timezone).toString()
        : undefined,
      createdAtTo: sidebarFilter.createdRange?.end
        ? toZoned(sidebarFilter.createdRange?.end.add({ days: 1 }), timezone).toString() // End of day!
        : undefined,
      requestedFrom: sidebarFilter.requestedRange?.start
        ? toZoned(sidebarFilter.requestedRange?.start, timezone).toString()
        : undefined,
      requestedTo: sidebarFilter.requestedRange?.end
        ? toZoned(sidebarFilter.requestedRange?.end.add({ days: 1 }), timezone).toString() // End of day!
        : undefined,
      first: pagination.pageSize * (pagination.page - 1),
      max: pagination.pageSize,
      queueIds: sidebarFilter.queues.toString() || undefined,
      states: sidebarFilter.states.toString() || undefined,
      subtypes: subtypes?.length ? subtypes.join(',') : undefined,
      supervisorId: userId,
      types: types?.length ? types.join(',') : undefined,
    };
  }, [pagination, searchFilter, sidebarFilter, timezone, userId]);

  const [disableEmailNotificationsModal, setDisableEmailNotificationsModal] = useState<DisableEmailNotificationsModal>({
    state: 'ready',
  });

  const {
    data: requestsData,
    loading: requestsLoading,
    error: requestsError,
  } = useGetSupervisorRequestsQuery({
    fetchPolicy: 'network-only',
    variables: getSupervisorRequestsVariables,
  });

  const {
    data: queuesData,
    loading: queuesLoading,
    error: queuesError,
  } = useGetQueuesQuery({
    fetchPolicy: 'network-only',
  });

  const [updateStateOfSupervisorPTORequest, { loading: isPtoRequestStateUpdateInProgress }] =
    useUpdateStateOfSupervisorPtoRequestMutation();

  const [updateStateOfShiftSwapRequest, { loading: isShiftSwapRequestStateUpdateInProgress }] =
    useUpdateStateOfShiftSwapRequestMutation();

  const hasEditNotificationSettingsPermission = userHasPermissions([Permissions.EditNotificationSettings]);
  const notificationSettingsVariables = {
    userId,
    types: NotificationType.Email,
    features: NotificationFeature.SelfService,
  };

  const { data: { notificationSettings } = {}, loading: notificationSettingsLoading } = useGetNotificationSettingsQuery(
    {
      variables: notificationSettingsVariables,
      skip: !hasEditNotificationSettingsPermission,
    }
  );

  /**
   * As we are querying for specific type of notification for specific feature,
   * the response is expected to contain only 1 settings object.
   */
  const emailNotificationSettings = notificationSettings?.[0] ?? null;

  const [updateStateOfNotificationSettings] = useUpdateStateOfNotificationSettingsMutation();

  const supervisorRequests = requestsData?.supervisorRequests.requests ?? [];

  const queues = recordBy(queuesData?.queues ?? [], 'id');

  const loading = requestsLoading || queuesLoading || notificationSettingsLoading;
  const isMutationInProgress = isPtoRequestStateUpdateInProgress || isShiftSwapRequestStateUpdateInProgress;
  const error = requestsError || queuesError;

  // Table with requests should be still rendered even though notificationSettings request has failed.
  const skipRendering = (loading && !supervisorRequests.length) || error;

  const handlePageChange = (page: number, pageSize?: number) => {
    setPagination((state) => ({
      ...state,
      page,
      pageSize: pageSize ?? state.pageSize,
    }));
  };

  const handleViewRequest = (requestId: string, requestType: RequestType) => {
    setIsFilterSidebarOpen(false);

    routerHistory.push(`${PATH_REQUESTS}/${REQUEST_URL_KEYS[requestType]}/${requestId}`);
  };

  const handleCancelViewRequest = () => {
    routerHistory.push(PATH_REQUESTS);
  };

  const handleResolvePtoRequest = async (
    currentState: RequestState,
    newState: RequestState,
    refetchRequestQuery: () => Promise<ApolloQueryResult<GetSupervisorPtoRequestQuery>>,
    message?: string
  ) => {
    if (!requestIdToView) {
      return Promise.reject();
    }

    try {
      await updateStateOfSupervisorPTORequest({
        variables: {
          supervisorId: userId,
          ptoRequestId: requestIdToView,
          fromState: currentState,
          parameters: {
            message,
            ptoRequestState: newState,
          },
        },
        update: (cache: ApolloCache<InMemoryCache>, result) => {
          const updatedPTORequest = result.data?.updateStateOfSupervisorPTORequest;

          if (!updatedPTORequest) {
            return Promise.reject();
          }

          const requests = cache.readQuery<GetSupervisorRequestsQueryResult['data']>({
            query: GetSupervisorRequestsDocument,
            variables: getSupervisorRequestsVariables,
          });

          const updatedRequestIndex =
            requests?.supervisorRequests?.requests.findIndex(
              ({ id, type }) => id === requestIdToView && type === typeOfRequestToView
            ) ?? -1;

          if (updatedRequestIndex > -1) {
            // Update PTO request object for table view cached data only when such request is present in table (could happen when opened from link sent in e-mail)
            const newRequests = cloneDeep(requests?.supervisorRequests.requests ?? []);

            newRequests[updatedRequestIndex] = {
              ...newRequests[updatedRequestIndex],
              approver: updatedPTORequest.approver,
              state: updatedPTORequest.state as unknown as RequestState,
            };

            cache.writeQuery({
              query: GetSupervisorRequestsDocument,
              variables: getSupervisorRequestsVariables,
              data: {
                supervisorRequests: {
                  ...requests?.supervisorRequests,
                  requests: newRequests,
                },
              },
            });
          }

          // Update PTO request object for detail view request's cached data
          cache.writeQuery({
            query: GetSupervisorPtoRequestDocument,
            variables: {
              supervisorId: userId,
              ptoRequestId: requestIdToView,
            },
            data: {
              supervisorPtoRequest: updatedPTORequest,
            },
          });

          return Promise.resolve();
        },
      });

      return Promise.resolve();
    } catch (e) {
      // error message provided by response provider

      try {
        const {
          data: { supervisorPtoRequest },
        } = await refetchRequestQuery();

        // the request has been resolved in meantime
        if ((supervisorPtoRequest.state as unknown as RequestState) !== currentState) {
          // update cache of re-fetched request within list if present
          const requests = client.cache.readQuery<GetSupervisorRequestsQueryResult['data']>({
            query: GetSupervisorRequestsDocument,
            variables: getSupervisorRequestsVariables,
          });

          const updatedRequestIndex =
            requests?.supervisorRequests?.requests.findIndex(
              ({ id, type }) => id === requestIdToView && type === typeOfRequestToView
            ) ?? -1;

          if (updatedRequestIndex > -1) {
            // Update PTO request object for table view cached data only when such request is present in table (could happen when opened from link sent in e-mail)
            const newRequests = cloneDeep(requests?.supervisorRequests.requests ?? []);

            newRequests[updatedRequestIndex] = {
              ...newRequests[updatedRequestIndex],
              approver: supervisorPtoRequest.approver,
              state: supervisorPtoRequest.state as unknown as RequestState,
            };

            client.cache.writeQuery({
              query: GetSupervisorRequestsDocument,
              variables: getSupervisorRequestsVariables,
              data: {
                supervisorRequests: {
                  ...requests?.supervisorRequests,
                  requests: newRequests,
                },
              },
            });
          }
        }
      } catch (err) {
        // error message provided by response provider
      }

      return Promise.reject(e);
    }
  };

  const handleResolveShiftSwapRequest = async (
    currentState: RequestState,
    action: ShiftSwapRequestAction.Approve | ShiftSwapRequestAction.Decline,
    refetchRequestQuery: () => Promise<ApolloQueryResult<GetSupervisorShiftSwapRequestQuery>>,
    message?: string
  ) => {
    if (!requestIdToView) {
      return Promise.reject();
    }

    try {
      await updateStateOfShiftSwapRequest({
        variables: {
          agentId: userId,
          requestId: requestIdToView,
          action,
          controller: ShiftSwapRequestController.Supervisors,
          parameters: {
            fromState: currentState,
            message,
          },
        },
        update: (cache: ApolloCache<InMemoryCache>, result) => {
          const updatedShiftSwapRequest = result.data?.updateStateOfShiftSwapRequest;

          if (!updatedShiftSwapRequest) {
            return Promise.reject();
          }

          const requests = cache.readQuery<GetSupervisorRequestsQueryResult['data']>({
            query: GetSupervisorRequestsDocument,
            variables: getSupervisorRequestsVariables,
          });

          const updatedRequestIndex =
            requests?.supervisorRequests?.requests.findIndex(
              ({ id, type }) => id === requestIdToView && type === typeOfRequestToView
            ) ?? -1;

          if (updatedRequestIndex > -1) {
            // Update Shift Swap request object for table view cached data only when such request is present in table (could happen when opened from link sent in e-mail)
            const newRequests = cloneDeep(requests?.supervisorRequests.requests ?? []);

            newRequests[updatedRequestIndex] = {
              ...newRequests[updatedRequestIndex],
              approver: updatedShiftSwapRequest.approver,
              state: updatedShiftSwapRequest.state as unknown as RequestState,
            };

            cache.writeQuery({
              query: GetSupervisorRequestsDocument,
              variables: getSupervisorRequestsVariables,
              data: {
                supervisorRequests: {
                  ...requests?.supervisorRequests,
                  requests: newRequests,
                },
              },
            });
          }

          // Update Shift Swap request object for detail view request's cached data
          cache.writeQuery({
            query: GetSupervisorShiftSwapRequestDocument,
            variables: {
              supervisorId: userId,
              shiftSwapRequestId: requestIdToView,
            },
            data: {
              supervisorShiftSwapRequest: updatedShiftSwapRequest,
            },
          });

          return Promise.resolve();
        },
      });

      return Promise.resolve();
    } catch (e) {
      // error message provided by response provider

      try {
        const {
          data: { supervisorShiftSwapRequest },
        } = await refetchRequestQuery();

        // the request has been resolved in meantime
        if ((supervisorShiftSwapRequest.state as unknown as RequestState) !== currentState) {
          // update cache of re-fetched request within list if present
          const requests = client.cache.readQuery<GetSupervisorRequestsQueryResult['data']>({
            query: GetSupervisorRequestsDocument,
            variables: getSupervisorRequestsVariables,
          });

          const updatedRequestIndex =
            requests?.supervisorRequests?.requests.findIndex(
              ({ id, type }) => id === requestIdToView && type === typeOfRequestToView
            ) ?? -1;

          if (updatedRequestIndex > -1) {
            // Update Shift Swap request object for table view cached data only when such request is present in table (could happen when opened from link sent in e-mail)
            const newRequests = cloneDeep(requests?.supervisorRequests.requests ?? []);

            newRequests[updatedRequestIndex] = {
              ...newRequests[updatedRequestIndex],
              approver: supervisorShiftSwapRequest.approver,
              state: supervisorShiftSwapRequest.state as unknown as RequestState,
            };

            client.cache.writeQuery({
              query: GetSupervisorRequestsDocument,
              variables: getSupervisorRequestsVariables,
              data: {
                supervisorRequests: {
                  ...requests?.supervisorRequests,
                  requests: newRequests,
                },
              },
            });
          }
        }
      } catch (err) {
        // error message provided by response provider
      }

      return Promise.reject(e);
    }
  };

  const handleChangeNotificationSettings = async (enabled: boolean) => {
    if (!emailNotificationSettings) {
      return;
    }

    if (!enabled && disableEmailNotificationsModal.state === 'ready') {
      setDisableEmailNotificationsModal({
        state: 'open',
      });

      return;
    }

    try {
      await updateStateOfNotificationSettings({
        variables: {
          userId,
          notificationSettingsId: emailNotificationSettings.id,
          parameters: {
            enabled,
          },
        },
        update: (cache: ApolloCache<InMemoryCache>, result) => {
          const updatedEmailSettings = result.data?.updateStateOfNotificationSettings;

          if (updatedEmailSettings) {
            // Considering only 1 notificationSetting is being queried based on provided variables.
            cache.writeQuery({
              query: GetNotificationSettingsDocument,
              variables: notificationSettingsVariables,
              data: {
                notificationSettings: [updatedEmailSettings],
              },
            });
          }
        },
      });

      setDisableEmailNotificationsModal({ state: 'ready' });
    } catch (e) {
      // error message provided by response provider
    }
  };

  const dismissDisableEmailNotificationsModal = () => {
    setDisableEmailNotificationsModal({ state: 'ready' });
  };

  const handleToggleFilterSidebar = () => {
    setPreOpenedItemIds([]);

    if (isFilterSidebarOpen) {
      closeSidebar();
      setIsFilterSidebarOpen(false);
    } else {
      setIsFilterSidebarOpen(true);

      if (requestIdToView) {
        routerHistory.push(PATH_REQUESTS);
      }

      openSidebar();
    }
  };

  const handleOpenFilterSidebarItem = (preOpenItemId: string) => {
    if (requestIdToView) {
      routerHistory.push(PATH_REQUESTS);
    }

    setIsFilterSidebarOpen(true);
    setPreOpenedItemIds([preOpenItemId]);
    openSidebar();
  };

  const handleCancelFilterSidebar = () => {
    closeSidebar();
    setIsFilterSidebarOpen(false);
    setPreOpenedItemIds([]);
  };

  const resetPagination = () => {
    handlePageChange(1);
  };

  const handleSearch = (
    { agentName, agentSurname }: RequestsHeaderFilterType,
    form: FormApi<RequestsHeaderFilterType>
  ) => {
    // Storing form reference to header search
    headerForm.current = form;

    const values = {
      agentName: agentName?.trim(),
      agentSurname: agentSurname?.trim(),
    };

    // Will call API twice, because it is not batched, React v18 should change that
    resetPagination();
    setSearchFilter(values);
  };

  const handleFilter = (filter: RequestsSidebarFilterState) => {
    // Will call API twice, because it is not batched, React v18 should change that
    resetPagination();
    setSidebarFilter(filter);
  };

  const handleQuickFilter = (quickFilterValues: RequestsSidebarFilterState) => {
    // Reset name/surname filter only when used before
    headerForm.current?.reset();

    handleFilter(quickFilterValues);
  };

  const formatter = useDateFormatter({ dateStyle: 'short', timeZone: timezone });

  const ActiveFilters: React.FC = () => (
    <ActiveFiltersNew
      config={[
        {
          dataTestId: 'requests-active-sidebar-filter-created-range',
          defaultValue: null,
          id: '1',
          key: 'createdRange',
          name: t('schedule.request.createdRange'),
          value:
            sidebarFilter.createdRange?.start && sidebarFilter.createdRange?.end
              ? `${formatter.format(sidebarFilter.createdRange.start?.toDate(timezone))} - ${formatter.format(
                  sidebarFilter.createdRange?.end.toDate(timezone)
                )}`
              : '',
        },
        {
          dataTestId: 'requests-active-sidebar-filter-requested-range',
          defaultValue: null,
          id: '2',
          key: 'requestedRange',
          name: t('schedule.request.requestedRange'),
          value:
            sidebarFilter.requestedRange?.start && sidebarFilter.requestedRange?.end
              ? `${formatter.format(sidebarFilter.requestedRange.start?.toDate(timezone))} - ${formatter.format(
                  sidebarFilter.requestedRange?.end.toDate(timezone)
                )}`
              : '',
        },
        {
          dataTestId: 'requests-active-sidebar-filter-request-type',
          defaultValue: [],
          id: '3',
          key: 'subtypes',
          name: t('schedule.request.requestType.requestType'),
          value: getActiveFilterValue(subtypeItems(t), sidebarFilter.subtypes),
        },
        {
          dataTestId: 'requests-active-sidebar-filter-status',
          defaultValue: [],
          id: '4',
          key: 'states',
          name: t('dataImport.status.status'),
          value: getActiveFilterValue(stateItems(t), sidebarFilter.states),
        },
        {
          dataTestId: 'requests-active-sidebar-filter-queues',
          defaultValue: [],
          id: '5',
          key: 'queues',
          name: t('queue.queues'),
          value: getActiveFilterValue(queuesData?.queues, sidebarFilter.queues),
        },
      ]}
      cancelFilterSidebar={handleCancelFilterSidebar}
      openFilterSidebarItem={handleOpenFilterSidebarItem}
      setSidebarFilter={setSidebarFilter}
      sidebarFilter={sidebarFilter}
    />
  );

  return (
    <>
      <Header title={t('requests.request', { count: 2 })} testId={TITLE_TAG} />

      <Panel>
        <Stack className="w-full" spacing={15}>
          <RequestsHeaderSearchForm onSubmit={handleSearch} />

          <ActiveFilters />

          <Stack className="mis-auto" spacing={15}>
            {hasEditNotificationSettingsPermission && (
              <Switch
                isSelected={emailNotificationSettings?.enabled ?? false}
                onChange={handleChangeNotificationSettings}
                isDisabled={!emailNotificationSettings}
                testId={EMAIL_NOTIFICATION_SWITCH_TAG}
              >
                {t('notification.emailNotifications')}
              </Switch>
            )}

            <Button
              isSelected={isFilterSidebarOpen}
              onClick={handleToggleFilterSidebar}
              testId={TOGGLE_FILTER_BUTTON_TAG}
            >
              <FilterIcon />
              {t('filter.filters')}
            </Button>
          </Stack>
        </Stack>
      </Panel>

      <Content testId={CONTAINER_TAG}>
        {(loading || isMutationInProgress) && <Progress withOverlay />}

        {!skipRendering && supervisorRequests.length > 0 && (
          <RequestsTable
            activeId={
              typeOfRequestToView && requestIdToView
                ? getRequestKey({ id: requestIdToView, type: typeOfRequestToView } as SupervisorRequestPreview)
                : undefined
            }
            onView={handleViewRequest}
            queues={queues as Record<string, Queue>}
            supervisorRequests={supervisorRequests}
          >
            <Pagination
              onPageChange={handlePageChange}
              selectedPage={pagination.page}
              selectedPageSize={pagination.pageSize}
              testId={LIST_PAGINATION_TAG}
              totalItems={requestsData?.supervisorRequests.totalCount || 0}
            />
          </RequestsTable>
        )}

        {!skipRendering && supervisorRequests.length === 0 && (
          <Center>
            <IllustratedMessage
              heading={
                isHeaderFilterActive || isSidebarFiltersActive
                  ? t('filter.noResultsMatch')
                  : t('schedule.request.noRequest')
              }
              icon={<SearchNoneIcon />}
            />
          </Center>
        )}
      </Content>

      <SidebarWrapper>
        {isSidebarOpen && requestIdToView && typeOfRequestToView === RequestType.Pto && !isFilterSidebarOpen && (
          <RequestsSidebarPTODetail
            key={requestIdToView} // should be responsible for resetting message state on requestId change
            handleCancel={handleCancelViewRequest}
            handleResolve={handleResolvePtoRequest}
            requestId={requestIdToView}
            queues={queues}
            handleQuickFilter={handleQuickFilter}
          />
        )}

        {isSidebarOpen && requestIdToView && typeOfRequestToView === RequestType.ShiftSwap && !isFilterSidebarOpen && (
          <RequestsSidebarShiftSwapDetail
            key={requestIdToView} // should be responsible for resetting message state on requestId change
            handleCancel={handleCancelViewRequest}
            handleResolve={handleResolveShiftSwapRequest}
            requestId={requestIdToView}
            queues={queues}
          />
        )}

        {isSidebarOpen && !requestIdToView && isFilterSidebarOpen && (
          <RequestsFilterSidebar
            handleCancel={handleCancelFilterSidebar}
            preOpenedItemIds={preOpenedItemIds}
            setPreOpenedItemIds={setPreOpenedItemIds}
            setSidebarFilter={handleFilter}
            sidebarFilter={sidebarFilter}
            queuesResult={{ data: queuesData } as QueuesResult}
          />
        )}
      </SidebarWrapper>
      <ModalConfirmation
        acceptCaption={t('layout.action.confirm')}
        cancelCaption={t('layout.action.cancel')}
        isOpen={disableEmailNotificationsModal.state === 'open'}
        onAccept={() => handleChangeNotificationSettings(false)}
        onCancel={dismissDisableEmailNotificationsModal}
        title={t('layout.warning')}
      >
        <div className="whitespace-pre-line">{t('notification.modalDisableEmailNotificationsForRequestsBody')}</div>
      </ModalConfirmation>
    </>
  );
};
